/* LMC Orlando — Financing page calculator (Chunk 14).
   Loaded only on /pages/financing via layout/theme.liquid.

   <lmc-financing-calc> reads three inputs (amount, term, APR) and
   writes the monthly estimate + recap to [data-fc-monthly] /
   [data-fc-detail-*]. Calculation is the standard amortization
   formula: monthly = principal * (rate/12) / (1 - (1 + rate/12)^-n).
   For 0% APR it degenerates to principal/term.

   Calculator is illustrative — final rate and term come from
   Affirm's instant decision at checkout. See SHOPIFY_ADMIN_TASKS.md §9.
*/

(function () {
  'use strict';

  class LMCFinancingCalc extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.update.bind(this);
      this.onAmountBlur = () => { this.normalizeAmount(); this.update(); };
      this.onAprBlur = () => { this.normalizeApr(); this.update(); };
    }

    connectedCallback() {
      this.amountEl = this.querySelector('[data-fc-amount]');
      this.termEl = this.querySelector('[data-fc-term]');
      this.aprEl = this.querySelector('[data-fc-apr]');
      this.monthlyEl = this.querySelector('[data-fc-monthly]');
      this.termLabelEl = this.querySelector('[data-fc-term-label]');
      this.detailAmountEl = this.querySelector('[data-fc-detail-amount]');
      this.detailTermEl = this.querySelector('[data-fc-detail-term]');
      this.detailAprEl = this.querySelector('[data-fc-detail-apr]');

      // Amount + APR are text inputs (so we can show "$4,999" / "9.9%"):
      // recompute live on input, then re-format + clamp on blur. Term is a
      // native <select>.
      if (this.amountEl) {
        this.amountEl.addEventListener('input', this.onInput);
        this.amountEl.addEventListener('blur', this.onAmountBlur);
      }
      if (this.termEl) this.termEl.addEventListener('change', this.onInput);
      if (this.aprEl) {
        this.aprEl.addEventListener('input', this.onInput);
        this.aprEl.addEventListener('blur', this.onAprBlur);
      }

      this.normalizeAmount();
      this.normalizeApr();
      this.update();
    }

    disconnectedCallback() {
      if (this.amountEl) {
        this.amountEl.removeEventListener('input', this.onInput);
        this.amountEl.removeEventListener('blur', this.onAmountBlur);
      }
      if (this.termEl) this.termEl.removeEventListener('change', this.onInput);
      if (this.aprEl) {
        this.aprEl.removeEventListener('input', this.onInput);
        this.aprEl.removeEventListener('blur', this.onAprBlur);
      }
    }

    // Strip formatting ("$4,999" -> 4999, "9.9%" -> 9.9) for the math.
    readAmount() {
      const parsed = Number(String((this.amountEl && this.amountEl.value) || '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    }
    readApr() {
      const parsed = Number(String((this.aprEl && this.aprEl.value) || '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    }

    // Re-display the amount with thousands separators, clamped to min/max.
    normalizeAmount() {
      if (!this.amountEl) return;
      const min = parseFloat(this.amountEl.dataset.fcMin) || 0;
      const max = parseFloat(this.amountEl.dataset.fcMax) || Infinity;
      const v = Math.min(Math.max(this.readAmount(), min), max);
      this.amountEl.value = Math.round(v).toLocaleString('en-US');
    }
    // Re-display the APR clamped to 0–100, trimming trailing zeros.
    normalizeApr() {
      if (!this.aprEl) return;
      const v = Math.min(Math.max(this.readApr(), 0), 100);
      this.aprEl.value = String(parseFloat(v.toFixed(2)));
    }

    update() {
      const amount = this.readAmount();
      const term = parseInt((this.termEl && this.termEl.value) || '0', 10);
      const apr = this.readApr();

      let monthly = 0;
      if (term > 0 && amount > 0) {
        if (apr <= 0) {
          monthly = amount / term;
        } else {
          // Standard amortization: M = P * r / (1 - (1 + r)^-n)
          const r = apr / 100 / 12;
          monthly = (amount * r) / (1 - Math.pow(1 + r, -term));
        }
      }

      if (this.monthlyEl) this.monthlyEl.textContent = this.formatMoney(monthly);
      if (this.termLabelEl) this.termLabelEl.textContent = `${term} months`;
      if (this.detailAmountEl) this.detailAmountEl.textContent = this.formatMoney(amount);
      if (this.detailTermEl) this.detailTermEl.textContent = String(term);
      if (this.detailAprEl) this.detailAprEl.textContent = `${apr}%`;
    }

    formatMoney(value) {
      // Inline (Shopify-style) formatter — uses Shopify.formatMoney
      // when available AND money_format is a usable string. If the
      // theme settings are misconfigured (empty / non-string format),
      // skip Shopify.formatMoney to avoid passing it bad input and
      // fall back to a simple "$X,XXX" rounded format (monthly
      // estimates don't need cent-precision).
      const fmt = window.Shopify && window.Shopify.money_format;
      const hasValidFmt = typeof fmt === 'string' && fmt.trim().length > 0;
      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function' &&
        hasValidFmt
      ) {
        const cents = Math.round(value * 100);
        return window.Shopify.formatMoney(cents, fmt);
      }
      const rounded = Math.round(value);
      const withSeparators = String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `$${withSeparators}`;
    }
  }

  if (!customElements.get('lmc-financing-calc')) {
    customElements.define('lmc-financing-calc', LMCFinancingCalc);
  }
})();
